import { OnlineOrder, OnlineOrderingStore } from 'kiolyn-api';
import { decorate, observable } from 'mobx';
import { useLocalStore } from 'mobx-react-lite';
import React, { createContext, FC, ReactElement, useContext } from 'react';
import { ViewState } from '../domain/models/ViewState';
import OnlineOrderingRepository from '../interfaces/OnlineOrderingRepository';

class AppleWalletStore {
  viewState = ViewState.LOADING;
  order?: OnlineOrder;
  store?: OnlineOrderingStore;
  orderId?: string;

  constructor(
    private readonly storeId?: string,
    orderId?: string,
    private readonly orderingRepository?: OnlineOrderingRepository
  ) {
    this.orderId = orderId;
  }

  public async reload(): Promise<void> {
    if (!this.orderingRepository || !this.storeId || !this.orderId) return;
    this.viewState = ViewState.LOADING;
    try {
      const [store, order] = await Promise.all([
        this.orderingRepository.loadStore(this.storeId),
        this.orderingRepository.loadOrderForAppleWallet(
          this.storeId,
          this.orderId
        )
      ]);
      this.order = order?.id ? order : undefined;
      this.store = store;
      if (this.store && this.order) {
        this.viewState = ViewState.OK;
        return;
      }

      this.viewState = ViewState.ERROR;
    } catch (err) {
      console.error(err);
      this.viewState = ViewState.ERROR;
    }
  }
}

decorate(AppleWalletStore, {
  viewState: observable,
  order: observable
});

interface AppleWalletProviderProps {
  children: ReactElement;
  storeId: string;
  orderId: string;
  onlineOrderingRepository: OnlineOrderingRepository;
}

export const AppleWalletProvider: FC<AppleWalletProviderProps> = ({
  children,
  storeId,
  orderId,
  onlineOrderingRepository
}) => {
  const store = useLocalStore(
    () => new AppleWalletStore(storeId, orderId, onlineOrderingRepository)
  );
  store.reload();
  return (
    <AppleWalletContext.Provider value={store}>
      {children}
    </AppleWalletContext.Provider>
  );
};

export const AppleWalletContext = createContext(new AppleWalletStore('', ''));
export const useAppleWalletStore = () => useContext(AppleWalletContext);
